<template>
    <v-chip
        v-if="!store.isLoggedIn"
        class="user-menu-trigger"
        color="white"
        variant="outlined"
        size="large"
        density="comfortable"
        data-testid="login-button"
        @click="openLoginModal"
    >
        <span class="trigger-label">Prijava</span>
        <template #append>
            <v-icon size="22">mdi-account-circle-outline</v-icon>
        </template>
    </v-chip>

    <v-menu
        v-else-if="$vuetify.display.smAndUp"
        v-model="menuOpen"
        location="bottom end"
        offset="8"
        :close-on-content-click="true"
    >
        <template #activator="{ props: activatorProps }">
            <v-chip
                v-bind="activatorProps"
                class="user-menu-trigger"
                color="white"
                variant="outlined"
                size="large"
                density="comfortable"
                data-testid="user-menu-trigger"
            >
                <span class="trigger-label">{{ store.user?.username }}</span>
                <template #append>
                    <v-avatar size="24" color="primary">
                        <v-img
                            v-if="store.user?.avatarUrl"
                            :src="store.user.avatarUrl"
                            :alt="store.user.username"
                        />
                        <v-icon v-else size="18">mdi-account</v-icon>
                    </v-avatar>
                </template>
            </v-chip>
        </template>

        <UserMenuPanel
            :can-create-game="canCreateGame"
            :drafts-count="draftsCount"
            @logout="handleLogout"
        />
    </v-menu>

    <template v-else>
        <v-chip
            class="user-menu-trigger"
            color="white"
            variant="outlined"
            size="large"
            density="comfortable"
            data-testid="user-menu-trigger"
            @click="menuOpen = true"
        >
            <span class="trigger-label">{{ store.user?.username }}</span>
            <template #append>
                <v-avatar size="24" color="primary">
                    <v-img
                        v-if="store.user?.avatarUrl"
                        :src="store.user.avatarUrl"
                        :alt="store.user.username"
                    />
                    <v-icon v-else size="18">mdi-account</v-icon>
                </v-avatar>
            </template>
        </v-chip>
        <v-bottom-sheet v-model="menuOpen" data-testid="user-menu-sheet">
            <UserMenuPanel
                :can-create-game="canCreateGame"
                :drafts-count="draftsCount"
                full-width
                @logout="handleLogout"
            />
        </v-bottom-sheet>
    </template>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';

import { Role } from '../../models/User';
import { useUserStore } from '../../stores/userStore';
import { useModal } from '../../modals/useModal';
import { ModalCode } from '../../modals/ModalCode';
import { getDraftGames } from '../../services/api/gamesService';
import { logError } from '../../utils/logger';
import UserMenuPanel from './UserMenuPanel.vue';

const store = useUserStore();
const route = useRoute();
const router = useRouter();
const modalStore = useModal();
const { showModal } = modalStore;
const { activeModal } = storeToRefs(modalStore);

const menuOpen = ref(false);
const draftsCount = ref<number | null>(null);
let draftsLoaded = false;
// Tracks whether a modal was opened from inside the user menu sheet so we can
// restore the sheet once that modal closes (e.g. tapping a badge chip on mobile
// opens BadgeInfo behind the sheet otherwise — Vuetify overlay z-index > modal).
let reopenMenuAfterModal = false;

const canCreateGame = computed(() => store.isLoggedIn && store.role !== Role.Player);

function openLoginModal() {
    showModal(ModalCode.Login);
}

async function handleLogout() {
    menuOpen.value = false;
    await store.logout();
    await router.push('/');
}

// Close the overlay whenever the route changes — covers both desktop v-menu
// (where close-on-content-click usually handles it but mobile sheet doesn't).
watch(() => route.fullPath, () => {
    menuOpen.value = false;
    reopenMenuAfterModal = false;
});

// When a modal opens while the user-menu sheet is up, hide the sheet so the
// modal isn't trapped behind it; reopen the sheet once that modal closes.
watch(activeModal, (newModal, oldModal) => {
    if (oldModal == null && newModal != null && menuOpen.value) {
        reopenMenuAfterModal = true;
        menuOpen.value = false;
        return;
    }

    if (oldModal != null && newModal == null && reopenMenuAfterModal) {
        reopenMenuAfterModal = false;
        menuOpen.value = true;
    }
});

// Fetch the draft count lazily on first open for users who can create games.
// Reset on logout so a re-login refetches.
watch(menuOpen, async (isOpen) => {
    if (!isOpen || draftsLoaded || !canCreateGame.value || !store.user) {
        return;
    }
    draftsLoaded = true;
    try {
        const drafts = await getDraftGames(store.user.id);
        draftsCount.value = drafts.length;
    } catch (error) {
        logError(error);
        draftsLoaded = false;
    }
});

watch(() => store.isLoggedIn, (isLoggedIn) => {
    if (!isLoggedIn) {
        draftsLoaded = false;
        draftsCount.value = null;
    }
});
</script>

<style lang="scss" scoped>
.user-menu-trigger {
    width: 140px;
    font-family: 'Inter Variable', 'Inter', sans-serif;

    @media (max-width: 600px) {
        width: 110px;
    }

    :deep(.v-chip__content) {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
    }

    :deep(.v-chip__append) {
        margin-inline-start: 4px;
    }

    .trigger-label {
        display: block;
        width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-align: left;
        text-overflow: clip;
        -webkit-mask-image: linear-gradient(to right, black calc(100% - 10px), transparent);
        mask-image: linear-gradient(to right, black calc(100% - 10px), transparent);
    }
}
</style>
